import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { AuthContext } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "./ui/table";
import { Badge } from "./ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import {
  ClipboardList, Plus, ArrowLeft, FileDown, Lock, Unlock, CheckCircle2, X as XIcon,
  UserPlus, PenLine, ListChecks, Clock, ChevronRight, Pencil, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import SignatureCanvas from "react-signature-canvas";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const todayStr = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`;
};
const nowTime = () => {
  const n = new Date();
  return `${String(n.getHours()).padStart(2, "0")}:${String(n.getMinutes()).padStart(2, "0")}`;
};
const fmtDate = (d) => {
  try { return new Date(d + "T00:00:00").toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return d; }
};
const fmtTime = (iso) => {
  try { return new Date(iso).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};
const CLOSED_CA = ["complete", "completed", "closed", "deleted", "resolved", "done"];
const isOpenCA = (t) => {
  const ca = t && t.corrective_action;
  return !!(ca && ca.linked_id && !CLOSED_CA.includes((ca.status || "").toLowerCase()));
};

export default function ToolboxTalks() {
  const { user } = useContext(AuthContext);
  const token = localStorage.getItem("token");
  const authHeader = { Authorization: `Bearer ${token}` };

  const canManage = ["admin", "company_owner", "site_manager"].includes(user?.role);
  const canSign = user?.role !== "view_only";
  const canReopen = ["admin", "company_owner"].includes(user?.role);

  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [search, setSearch] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [openFilter, setOpenFilter] = useState(searchParams.get("filter") === "open");
  const [talks, setTalks] = useState([]);
  const [topicOptions, setTopicOptions] = useState([]);

  const [view, setView] = useState("list"); // list | detail
  const [current, setCurrent] = useState(null);

  // dialogs
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ date: todayStr(), start_time: nowTime(), topics: [], customTopic: "" });
  const [showSign, setShowSign] = useState(false);
  const [signTarget, setSignTarget] = useState(null);
  const [showManual, setShowManual] = useState(false);
  const [manualForm, setManualForm] = useState({ name: "", company: "" });
  const [showCorrective, setShowCorrective] = useState(false);
  const [correctiveForm, setCorrectiveForm] = useState({ linked_type: "task", title: "", description: "", assignee_ref: "", assignee_name: "" });
  const [confirmClose, setConfirmClose] = useState(false);
  const sigRef = useRef(null);
  const nbRef = useRef(null);
  const attRef = useRef(null);
  const [dpInput, setDpInput] = useState("");
  const [nbInput, setNbInput] = useState("");
  const [editDp, setEditDp] = useState(null);
  const [editNb, setEditNb] = useState(null);

  useEffect(() => {
    fetch(`${API}/sites`, { headers: authHeader }).then((r) => r.ok && r.json()).then((d) => {
      if (d) { setSites(d); if (d.length) setSelectedSite(d[0].id); }
    });
    fetch(`${API}/toolbox-talks/topics/list`, { headers: authHeader }).then((r) => r.ok && r.json()).then((d) => d && setTopicOptions(d.topics || []));
  }, []); // eslint-disable-line

  const loadTalks = useCallback(async () => {
    if (!selectedSite) return;
    let url = `${API}/toolbox-talks/${selectedSite}`;
    if (startDate && endDate) url += `?start_date=${startDate}&end_date=${endDate}`;
    const r = await fetch(url, { headers: authHeader });
    if (r.ok) setTalks(await r.json());
  }, [selectedSite, startDate, endDate]); // eslint-disable-line

  useEffect(() => { loadTalks(); }, [loadTalks]);

  const refreshCurrent = async (id, site) => {
    const r = await fetch(`${API}/toolbox-talks/${site}/${id}`, { headers: authHeader });
    if (r.ok) setCurrent(await r.json());
  };

  const saveSection = async (field, arr) => {
    const r = await fetch(`${API}/toolbox-talks/${current.id}`, {
      method: "PUT", headers: { ...authHeader, "Content-Type": "application/json" },
      body: JSON.stringify({ [field]: arr }),
    });
    if (r.ok) { await refreshCurrent(current.id, current.site_id); }
    else { toast.error("Could not save"); }
  };

  const currentPoints = (field) => {
    if (field === "discussion_points") {
      return (current.discussion_points && current.discussion_points.length)
        ? [...current.discussion_points]
        : (current.discussion_notes ? [current.discussion_notes] : []);
    }
    return [...(current.new_business || [])];
  };

  const commitDp = async () => {
    const v = dpInput.trim(); if (!v) return;
    const arr = currentPoints("discussion_points");
    if (editDp !== null) arr[editDp] = v; else arr.push(v);
    await saveSection("discussion_points", arr); setDpInput(""); setEditDp(null);
  };
  const deleteDp = async (i) => { const arr = currentPoints("discussion_points"); arr.splice(i, 1); await saveSection("discussion_points", arr); };
  const commitNb = async () => {
    const v = nbInput.trim(); if (!v) return;
    const arr = currentPoints("new_business");
    if (editNb !== null) arr[editNb] = v; else arr.push(v);
    await saveSection("new_business", arr); setNbInput(""); setEditNb(null);
  };
  const deleteNb = async (i) => { const arr = currentPoints("new_business"); arr.splice(i, 1); await saveSection("new_business", arr); };

  const openDetail = async (talk) => {
    await refreshCurrent(talk.id, talk.site_id);
    setView("detail");
  };

  const createTalk = async () => {
    if (!createForm.topics.length) { toast.error("Add at least one topic"); return; }
    const r = await fetch(`${API}/toolbox-talks`, {
      method: "POST", headers: { ...authHeader, "Content-Type": "application/json" },
      body: JSON.stringify({
        site_id: selectedSite, date: createForm.date, start_time: createForm.start_time,
        topics: createForm.topics,
      }),
    });
    if (r.ok) {
      const t = await r.json();
      toast.success(`Toolbox talk ${t.record_id} created`);
      setShowCreate(false);
      setCreateForm({ date: todayStr(), start_time: nowTime(), topics: [], customTopic: "" });
      setCurrent(t); setView("detail"); loadTalks();
    } else { toast.error("Could not create talk"); }
  };

  const toggleTopic = (t) => setCreateForm((f) => ({
    ...f, topics: f.topics.includes(t) ? f.topics.filter((x) => x !== t) : [...f.topics, t],
  }));
  const addCustomTopic = () => {
    const t = createForm.customTopic.trim();
    if (t && !createForm.topics.includes(t)) setCreateForm((f) => ({ ...f, topics: [...f.topics, t], customTopic: "" }));
  };

  const excludeAttendee = async (user_ref) => {
    const r = await fetch(`${API}/toolbox-talks/${current.id}/exclude`, {
      method: "POST", headers: { ...authHeader, "Content-Type": "application/json" },
      body: JSON.stringify({ user_ref }),
    });
    if (r.ok) setCurrent(await r.json());
    else toast.error((await r.json()).detail || "Could not remove");
  };

  const addManual = async () => {
    if (!manualForm.name.trim()) { toast.error("Name required"); return; }
    const r = await fetch(`${API}/toolbox-talks/${current.id}/add-attendee`, {
      method: "POST", headers: { ...authHeader, "Content-Type": "application/json" },
      body: JSON.stringify(manualForm),
    });
    if (r.ok) { setCurrent(await r.json()); setShowManual(false); setManualForm({ name: "", company: "" }); }
    else toast.error("Could not add attendee");
  };

  const openSign = (att) => { setSignTarget(att); setShowSign(true); };
  const submitSign = async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) { toast.error("Please sign before confirming"); return; }
    const data = sigRef.current.getCanvas().toDataURL("image/png");
    const r = await fetch(`${API}/toolbox-talks/${current.id}/sign`, {
      method: "POST", headers: { ...authHeader, "Content-Type": "application/json" },
      body: JSON.stringify({ user_ref: signTarget.user_ref, signature_image: data }),
    });
    if (r.ok) { setCurrent(await r.json()); setShowSign(false); setSignTarget(null); toast.success("Signature captured"); }
    else toast.error((await r.json()).detail || "Could not sign");
  };

  const submitCorrective = async () => {
    if (!correctiveForm.title.trim()) { toast.error("Title required"); return; }
    if (correctiveForm.linked_type === "task" && !correctiveForm.assignee_name) { toast.error("Please assign the task to someone"); return; }
    const r = await fetch(`${API}/toolbox-talks/${current.id}/corrective-action`, {
      method: "POST", headers: { ...authHeader, "Content-Type": "application/json" },
      body: JSON.stringify(correctiveForm),
    });
    if (r.ok) { setCurrent(await r.json()); setShowCorrective(false); setCorrectiveForm({ linked_type: "task", title: "", description: "", assignee_ref: "", assignee_name: "" }); toast.success("Corrective action raised"); }
    else { const e = await r.json(); toast.error(e.detail || "Could not raise action"); }
  };

  const closeTalk = async () => {
    const r = await fetch(`${API}/toolbox-talks/${current.id}/close`, { method: "POST", headers: authHeader });
    if (r.ok) { setCurrent(await r.json()); setConfirmClose(false); loadTalks(); toast.success("Talk closed and locked"); }
    else toast.error("Could not close talk");
  };
  const reopenTalk = async () => {
    const r = await fetch(`${API}/toolbox-talks/${current.id}/reopen`, { method: "POST", headers: authHeader });
    if (r.ok) { setCurrent(await r.json()); loadTalks(); toast.success("Talk reopened"); }
    else toast.error((await r.json()).detail || "Could not reopen");
  };

  const downloadPdf = async (id) => {
    const r = await fetch(`${API}/toolbox-talks/${id}/pdf/export`, { headers: authHeader });
    if (!r.ok) { toast.error("Could not generate PDF"); return; }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `toolbox_talk_${id}.pdf`;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1500);
  };

  const filteredTalks = talks.filter((t) =>
    (!search || (t.topics || []).join(" ").toLowerCase().includes(search.toLowerCase())) &&
    (!openFilter || isOpenCA(t)));

  const setOpenFilterAndUrl = (on) => {
    setOpenFilter(on);
    const next = new URLSearchParams(searchParams);
    if (on) next.set("filter", "open"); else next.delete("filter");
    setSearchParams(next, { replace: true });
  };

  // ---------------- DETAIL VIEW ----------------
  if (view === "detail" && current) {
    const att = current.attendees || [];
    const signedN = att.filter((a) => a.signed).length;
    const closed = current.status === "closed";
    const ca = current.corrective_action;
    const correctiveAssignees = [
      { ref: user?.id, name: user?.username, label: `${user?.username} (you)` },
      ...att
        .filter((a) => a.name && a.user_ref !== user?.id)
        .map((a) => ({ ref: a.user_ref, name: a.name, label: a.company ? `${a.name} · ${a.company}` : a.name })),
    ];
    return (
      <div className="min-h-screen p-6 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <Button variant="ghost" onClick={() => { setView("list"); setCurrent(null); loadTalks(); }} data-testid="tbt-back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to list
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => downloadPdf(current.id)} data-testid="tbt-pdf-btn">
                <FileDown className="w-4 h-4 mr-2" /> Export PDF
              </Button>
              {closed && canReopen && (
                <Button variant="outline" onClick={reopenTalk} data-testid="tbt-reopen-btn">
                  <Unlock className="w-4 h-4 mr-2" /> Reopen
                </Button>
              )}
              {!closed && canManage && (
                <Button onClick={() => setConfirmClose(true)} className="bg-slate-800 hover:bg-slate-900" data-testid="tbt-close-btn">
                  <Lock className="w-4 h-4 mr-2" /> Close out
                </Button>
              )}
            </div>
          </div>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-2xl text-slate-800 flex items-center gap-2">
                  <ClipboardList className="w-6 h-6 text-blue-600" /> {current.record_id}
                </CardTitle>
                <Badge className={closed ? "bg-slate-700" : "bg-amber-500"} data-testid="tbt-status-badge">
                  {closed ? "Closed & locked" : "Open"}
                </Badge>
              </div>
              <p className="text-sm text-slate-500">
                {fmtDate(current.date)} · Start {current.start_time}
                {current.duration_minutes ? ` · ${current.duration_minutes} min` : ""} · Conducted by {current.conducted_by_name}
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-slate-500 text-xs uppercase">Topics</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {(current.topics || []).map((t) => <Badge key={t} variant="secondary">{t}</Badge>)}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Discussion Notes (Site Manager) */}
          <PointsSection
            title="Discussion Notes"
            subtitle="raised by the Site Manager"
            field="discussion_points"
            points={(current.discussion_points && current.discussion_points.length)
              ? current.discussion_points
              : (current.discussion_notes ? [current.discussion_notes] : [])}
            input={dpInput} setInput={setDpInput}
            editIdx={editDp} setEditIdx={setEditDp}
            onCommit={commitDp} onDelete={deleteDp}
            closed={closed} canManage={canManage}
            addLabel="Add point" placeholder="Type next discussion point…"
            navLabel="Move to New Business ▸" onNav={() => nbRef.current && nbRef.current.scrollIntoView({ behavior: "smooth" })}
            testid="dp"
          />

          {/* New Business (Contractors) */}
          <div ref={nbRef}>
            <PointsSection
              title="New Business"
              subtitle="items raised by contractors"
              field="new_business"
              points={current.new_business || []}
              input={nbInput} setInput={setNbInput}
              editIdx={editNb} setEditIdx={setEditNb}
              onCommit={commitNb} onDelete={deleteNb}
              closed={closed} canManage={canManage}
              addLabel="Add item" placeholder="Type new business item…"
              emptyText="None raised."
              navLabel="No new business → go to signing" onNav={() => attRef.current && attRef.current.scrollIntoView({ behavior: "smooth" })}
              testid="nb"
            />
          </div>

          {/* Attendees */}
          <Card ref={attRef}>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <PenLine className="w-5 h-5 text-blue-600" /> Persons present
                  <span className="text-sm font-normal text-slate-500">({signedN} of {att.length} signed)</span>
                </CardTitle>
                {!closed && canManage && (
                  <Button size="sm" variant="outline" onClick={() => setShowManual(true)} data-testid="tbt-add-attendee-btn">
                    <UserPlus className="w-4 h-4 mr-1" /> Add attendee
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {att.length === 0 && (
                <p className="text-sm text-slate-500" data-testid="tbt-empty-attendees">
                  No QR check-ins found for this day. Use “Add attendee” to record who was briefed.
                </p>
              )}
              <div className="divide-y">
                {att.map((a) => (
                  <div key={a.user_ref} className="flex items-center justify-between py-3 gap-3" data-testid={`tbt-attendee-${a.user_ref}`}>
                    <div className="min-w-0">
                      <p className="font-medium text-slate-800 truncate">{a.name || "Unnamed"}</p>
                      <p className="text-xs text-slate-500 truncate">
                        {a.company || "—"}{a.manual ? " · added manually" : ""}
                        {a.signed && a.signed_at ? ` · signed ${fmtTime(a.signed_at)}` : ""}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {a.signed ? (
                        <span className="flex items-center text-green-600 text-sm" data-testid={`tbt-signed-${a.user_ref}`}>
                          <CheckCircle2 className="w-4 h-4 mr-1" /> Signed
                        </span>
                      ) : (
                        <Badge variant="outline" className="text-amber-600 border-amber-300">Outstanding</Badge>
                      )}
                      {!closed && !a.signed && canSign && (
                        <Button size="sm" onClick={() => openSign(a)} data-testid={`tbt-sign-btn-${a.user_ref}`}>Sign</Button>
                      )}
                      {!closed && !a.signed && canManage && (
                        <Button size="sm" variant="ghost" onClick={() => excludeAttendee(a.user_ref)} data-testid={`tbt-exclude-btn-${a.user_ref}`}>
                          <XIcon className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Corrective action */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2"><ListChecks className="w-5 h-5 text-blue-600" /> Corrective action</CardTitle>
            </CardHeader>
            <CardContent>
              {ca && ca.linked_id ? (
                <div className="flex items-center justify-between" data-testid="tbt-corrective-summary">
                  <div>
                    <p className="font-medium text-slate-800">{ca.title || "—"}</p>
                    {ca.assignee_name && <p className="text-xs text-slate-600">Assigned to: {ca.assignee_name}</p>}
                    <p className="text-xs text-slate-500 capitalize">Linked {ca.linked_type} · status: {ca.status}</p>
                  </div>
                  <Badge variant="secondary" className="capitalize">{ca.linked_type}</Badge>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-slate-500">No corrective action raised.</p>
                  {!closed && canManage && (
                    <Button size="sm" variant="outline" onClick={() => setShowCorrective(true)} data-testid="tbt-corrective-btn">
                      <Plus className="w-4 h-4 mr-1" /> Raise action
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sign dialog (full-screen pad) */}
        <Dialog open={showSign} onOpenChange={setShowSign}>
          <DialogContent className="max-w-lg" data-testid="tbt-sign-dialog">
            <DialogHeader>
              <DialogTitle>{signTarget?.name}</DialogTitle>
              <DialogDescription>
                I confirm I attended this toolbox talk and understood the matters discussed.
              </DialogDescription>
            </DialogHeader>
            <div className="border-2 border-dashed border-slate-300 rounded-lg bg-white">
              <SignatureCanvas ref={sigRef} penColor="#0D1B2A"
                canvasProps={{ className: "w-full", height: 200, "data-testid": "tbt-sign-canvas" }} />
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => sigRef.current && sigRef.current.clear()} data-testid="tbt-sign-clear">Clear</Button>
              <Button onClick={submitSign} data-testid="tbt-sign-confirm">Confirm & sign</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Manual attendee dialog */}
        <Dialog open={showManual} onOpenChange={setShowManual}>
          <DialogContent data-testid="tbt-manual-dialog">
            <DialogHeader><DialogTitle>Add attendee</DialogTitle>
              <DialogDescription>Record someone who was briefed but isn’t in today’s QR check-in.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={manualForm.name} onChange={(e) => setManualForm({ ...manualForm, name: e.target.value })} data-testid="tbt-manual-name" /></div>
              <div><Label>Company (optional)</Label><Input value={manualForm.company} onChange={(e) => setManualForm({ ...manualForm, company: e.target.value })} data-testid="tbt-manual-company" /></div>
            </div>
            <DialogFooter><Button onClick={addManual} data-testid="tbt-manual-save">Add</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Corrective dialog */}
        <Dialog open={showCorrective} onOpenChange={setShowCorrective}>
          <DialogContent data-testid="tbt-corrective-dialog">
            <DialogHeader><DialogTitle>Raise corrective action</DialogTitle>
              <DialogDescription>Creates a linked record in Tasks or Defects.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Type</Label>
                <Select value={correctiveForm.linked_type} onValueChange={(v) => setCorrectiveForm({ ...correctiveForm, linked_type: v })}>
                  <SelectTrigger data-testid="tbt-corrective-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="task">Task (default)</SelectItem>
                    <SelectItem value="defect">Defect (escalate)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Title</Label><Input value={correctiveForm.title} onChange={(e) => setCorrectiveForm({ ...correctiveForm, title: e.target.value })} data-testid="tbt-corrective-title" /></div>
              {correctiveForm.linked_type === "task" && (
                <div>
                  <Label>Assigned to</Label>
                  <Select value={correctiveForm.assignee_ref}
                    onValueChange={(v) => {
                      const opt = correctiveAssignees.find((o) => o.ref === v);
                      setCorrectiveForm({ ...correctiveForm, assignee_ref: v, assignee_name: opt ? opt.name : "" });
                    }}>
                    <SelectTrigger data-testid="tbt-corrective-assignee"><SelectValue placeholder="Select a person from this toolbox talk" /></SelectTrigger>
                    <SelectContent>
                      {correctiveAssignees.map((o) => (
                        <SelectItem key={o.ref} value={o.ref}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">Only people on this toolbox talk (plus you) can be assigned.</p>
                </div>
              )}
              <div><Label>Description (optional)</Label><Textarea value={correctiveForm.description} onChange={(e) => setCorrectiveForm({ ...correctiveForm, description: e.target.value })} data-testid="tbt-corrective-desc" /></div>
            </div>
            <DialogFooter><Button onClick={submitCorrective} data-testid="tbt-corrective-save">Raise</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Confirm close */}
        <Dialog open={confirmClose} onOpenChange={setConfirmClose}>
          <DialogContent data-testid="tbt-close-dialog">
            <DialogHeader><DialogTitle>Close out this talk?</DialogTitle>
              <DialogDescription>
                {signedN < att.length
                  ? `${att.length - signedN} attendee(s) have not signed — they’ll be recorded as “Outstanding”. `
                  : ""}
                Once closed the record is permanently locked.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setConfirmClose(false)}>Cancel</Button>
              <Button className="bg-slate-800 hover:bg-slate-900" onClick={closeTalk} data-testid="tbt-close-confirm">Close & lock</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ---------------- LIST VIEW ----------------
  return (
    <div className="min-h-screen p-6 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <ClipboardList className="w-9 h-9 text-blue-600" />
            <div>
              <h1 className="text-3xl font-bold text-slate-800">Toolbox Talks</h1>
              <p className="text-slate-600">Record safety briefings — who was briefed, what was discussed, what came of it.</p>
            </div>
          </div>
          {canManage && selectedSite && (
            <Button onClick={() => setShowCreate(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="tbt-new-btn">
              <Plus className="w-4 h-4 mr-2" /> New toolbox talk
            </Button>
          )}
        </div>

        {filteredTalks.filter(isOpenCA).length > 0 && (
          <div className="mb-4 flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3" data-testid="tbt-open-actions-banner">
            <ListChecks className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-800">
              <span className="font-semibold">{filteredTalks.filter(isOpenCA).length}</span> open corrective action{filteredTalks.filter(isOpenCA).length === 1 ? "" : "s"} on this site — follow up so nothing slips through.
            </p>
          </div>
        )}

        <Card className="mb-6">
          <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label>Site</Label>
              <Select value={selectedSite} onValueChange={setSelectedSite}>
                <SelectTrigger data-testid="tbt-site-select"><SelectValue placeholder="Select site" /></SelectTrigger>
                <SelectContent>
                  {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>From</Label><Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="tbt-from-date" /></div>
            <div><Label>To</Label><Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="tbt-to-date" /></div>
            <div><Label>Topic search</Label><Input placeholder="e.g. heights" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="tbt-search" /></div>
          </CardContent>
        </Card>

        <div className="mb-4 flex items-center gap-2">
          <span className="text-sm text-slate-600">Show:</span>
          <button onClick={() => setOpenFilterAndUrl(false)} data-testid="tbt-filter-all"
            className={`px-3 py-1 rounded-full text-sm border transition-colors ${!openFilter ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`}>
            All
          </button>
          <button onClick={() => setOpenFilterAndUrl(true)} data-testid="tbt-filter-open"
            className={`px-3 py-1 rounded-full text-sm border transition-colors inline-flex items-center gap-1.5 ${openFilter ? "bg-red-500 text-white border-red-500" : "bg-white text-slate-600 border-slate-300 hover:border-red-400"}`}>
            <span className={`w-2 h-2 rounded-full ${openFilter ? "bg-white" : "bg-red-500"}`} /> Open actions
          </button>
        </div>

        <Card>
          <CardContent className="pt-6">
            {filteredTalks.length === 0 ? (
              <div className="text-center py-12 text-slate-500" data-testid="tbt-empty-list">
                <ClipboardList className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                {openFilter ? (
                  <p>No open corrective actions on this site. 🎉</p>
                ) : (
                  <>
                    <p>No toolbox talks yet for this site.</p>
                    {canManage && <p className="text-sm">Click “New toolbox talk” to record one.</p>}
                  </>
                )}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Record</TableHead><TableHead>Date</TableHead>
                    <TableHead>Conducted by</TableHead><TableHead>Topics</TableHead>
                    <TableHead>Signed</TableHead><TableHead>Action</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTalks.map((t) => {
                    const n = (t.attendees || []).length;
                    const s = (t.attendees || []).filter((a) => a.signed).length;
                    return (
                      <TableRow key={t.id} className="cursor-pointer hover:bg-slate-50" onClick={() => openDetail(t)} data-testid={`tbt-row-${t.id}`}>
                        <TableCell className="font-medium">{t.record_id}</TableCell>
                        <TableCell>{fmtDate(t.date)}</TableCell>
                        <TableCell>{t.conducted_by_name}</TableCell>
                        <TableCell className="max-w-[220px] truncate">{(t.topics || []).join(", ")}</TableCell>
                        <TableCell>{s}/{n}</TableCell>
                        <TableCell>
                          {isOpenCA(t)
                            ? <Badge className="bg-red-500 hover:bg-red-500" data-testid={`tbt-open-action-${t.id}`}>Open</Badge>
                            : (t.corrective_action && t.corrective_action.linked_id
                                ? <span className="text-xs text-green-600">Closed</span>
                                : <span className="text-xs text-slate-400">—</span>)}
                        </TableCell>
                        <TableCell>
                          <Badge className={t.status === "closed" ? "bg-slate-700" : "bg-amber-500"}>
                            {t.status === "closed" ? "Closed" : "Open"}
                          </Badge>
                        </TableCell>
                        <TableCell><ChevronRight className="w-4 h-4 text-slate-400" /></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="tbt-create-dialog">
          <DialogHeader>
            <DialogTitle>New toolbox talk</DialogTitle>
            <DialogDescription>Attendees auto-fill from today’s QR check-ins for the selected site.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Date</Label><Input type="date" value={createForm.date} onChange={(e) => setCreateForm({ ...createForm, date: e.target.value })} data-testid="tbt-create-date" /></div>
              <div><Label className="flex items-center gap-1"><Clock className="w-3 h-3" /> Start time</Label><Input type="time" value={createForm.start_time} onChange={(e) => setCreateForm({ ...createForm, start_time: e.target.value })} data-testid="tbt-create-time" /></div>
            </div>
            <div>
              <Label>Topics</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {topicOptions.map((t) => (
                  <button key={t} type="button" onClick={() => toggleTopic(t)} data-testid={`tbt-topic-${t}`}
                    className={`px-3 py-1 rounded-full text-sm border transition-colors ${createForm.topics.includes(t) ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-300 hover:border-blue-400"}`}>
                    {t}
                  </button>
                ))}
              </div>
              {createForm.topics.filter((t) => !topicOptions.includes(t)).map((t) => (
                <Badge key={t} className="mt-2 mr-1 bg-blue-600">{t}<button onClick={() => toggleTopic(t)} className="ml-1">×</button></Badge>
              ))}
              <div className="flex gap-2 mt-2">
                <Input placeholder="Add custom topic" value={createForm.customTopic}
                  onChange={(e) => setCreateForm({ ...createForm, customTopic: e.target.value })}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomTopic(); } }}
                  data-testid="tbt-custom-topic" />
                <Button type="button" variant="outline" onClick={addCustomTopic} data-testid="tbt-add-custom-topic">Add</Button>
              </div>
            </div>
            <p className="text-xs text-slate-500">You’ll add discussion notes and new business as numbered points on the next screen.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={createTalk} className="bg-blue-600 hover:bg-blue-700" data-testid="tbt-create-save">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PointsSection({ title, subtitle, points, input, setInput, editIdx, setEditIdx,
  onCommit, onDelete, closed, canManage, addLabel, placeholder, navLabel, onNav, emptyText, testid }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <ListChecks className="w-5 h-5 text-blue-600" /> {title}
          <span className="text-sm font-normal text-slate-500">({subtitle})</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {points.length === 0 && (
          <p className="text-sm text-slate-500" data-testid={`tbt-${testid}-empty`}>{emptyText || "No points added yet."}</p>
        )}
        <ol className="space-y-2">
          {points.map((p, i) => (
            <li key={i} className="flex items-start gap-2" data-testid={`tbt-${testid}-point-${i}`}>
              <span className="text-slate-400 font-medium w-5 flex-shrink-0">{i + 1}.</span>
              <span className="text-slate-700 flex-1 whitespace-pre-wrap">{p}</span>
              {!closed && canManage && (
                <span className="flex gap-1 flex-shrink-0">
                  <button onClick={() => { setInput(p); setEditIdx(i); }} className="text-slate-400 hover:text-blue-600" data-testid={`tbt-${testid}-edit-${i}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => onDelete(i)} className="text-slate-400 hover:text-red-600" data-testid={`tbt-${testid}-delete-${i}`}><Trash2 className="w-4 h-4" /></button>
                </span>
              )}
            </li>
          ))}
        </ol>
        {!closed && canManage && (
          <>
            <Textarea rows={2} value={input} placeholder={placeholder}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); onCommit(); } }}
              data-testid={`tbt-${testid}-input`} />
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={onCommit} className="bg-blue-600 hover:bg-blue-700" data-testid={`tbt-${testid}-add`}>
                <Plus className="w-4 h-4 mr-1" /> {editIdx !== null ? "Save point" : addLabel}
              </Button>
              {editIdx !== null && (
                <Button size="sm" variant="ghost" onClick={() => { setInput(""); setEditIdx(null); }} data-testid={`tbt-${testid}-cancel-edit`}>Cancel</Button>
              )}
              {navLabel && (
                <Button size="sm" variant="outline" onClick={onNav} data-testid={`tbt-${testid}-nav`}>{navLabel}</Button>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
